import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { isSupabaseConfigured, missingSupabaseEnvMessage, supabase } from '@/lib/supabase'
import { recordSignIn } from '@/lib/signInTelemetry'
import { AuthContext, type AuthContextValue } from './auth'

function callbackRedirect(path = '/auth/callback') {
  if (typeof window === 'undefined') return undefined
  return `${window.location.origin}${path}`
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(isSupabaseConfigured)
  // Track which user-ids we've already recorded telemetry for in this session
  // so we don't double-log on tab focus / token refresh events.
  const recordedRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!isSupabaseConfigured) return

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setLoading(false)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s)
      if (event === 'SIGNED_IN' && s?.user && !recordedRef.current.has(s.user.id)) {
        recordedRef.current.add(s.user.id)
        void recordSignIn(s.user)
      }
      if (event === 'SIGNED_OUT') {
        recordedRef.current.clear()
      }
    })
    return () => sub.subscription.unsubscribe()
  }, [])

  const signIn: AuthContextValue['signIn'] = async (email, password) => {
    if (!isSupabaseConfigured) return { error: new Error(missingSupabaseEnvMessage) }
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error: error ?? null }
  }

  const signUp: AuthContextValue['signUp'] = async (email, password, fullName) => {
    if (!isSupabaseConfigured) return { error: new Error(missingSupabaseEnvMessage), needsConfirmation: false }
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: callbackRedirect(),
        data: fullName ? { full_name: fullName } : undefined,
      },
    })
    if (error) return { error, needsConfirmation: false }
    // If the project has email confirmation on, Supabase returns a user but no session.
    const needsConfirmation = !data.session
    return { error: null, needsConfirmation }
  }

  const signOut = async () => {
    if (!isSupabaseConfigured) return
    await supabase.auth.signOut()
  }

  const resetPasswordForEmail: AuthContextValue['resetPasswordForEmail'] = async (email) => {
    if (!isSupabaseConfigured) return { error: new Error(missingSupabaseEnvMessage) }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: callbackRedirect('/reset-password'),
    })
    return { error: error ?? null }
  }

  const updatePassword: AuthContextValue['updatePassword'] = async (password) => {
    if (!isSupabaseConfigured) return { error: new Error(missingSupabaseEnvMessage) }
    const { error } = await supabase.auth.updateUser({ password })
    return { error: error ?? null }
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        loading,
        signIn,
        signUp,
        signOut,
        resetPasswordForEmail,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
